import { offlineSyncService } from "../services/offlineSync.service";
import {
  countVehicles,
  countVehiclesBySource,
  getMeta,
  isQuotaError,
  openOfflineDb,
  pruneVehiclesNotInSet,
  putVehicleBatch,
  setMeta,
  deleteOfflineDatabase,
} from "./offlineDb";
import {
  offlineDbName,
  readRememberedOfflineDbName,
  rememberOfflineDbName,
  resolveRepoIdentity,
} from "./offlineIdentity";
import { toStoredVehicle } from "./recordKeys";
import { clearWhatsAppRecipients, saveWhatsAppRecipients } from "./whatsappRecipientsCache";
import confirmationService from "../services/confirmation.service";

export const SYNC_STATUS = {
  IDLE: "idle",
  SYNCING: "syncing",
  COMPLETED: "completed",
  FAILED: "failed",
  OFFLINE_READY: "offline_ready",
};

const META_KEY = "state";
const OVERLAP_MS = 120000;
const RECORD_BATCH_LIMIT = 1000;

const listeners = new Set();
let runId = 0;
let abortController = null;
let snapshot = emptySnapshot();

function emptySnapshot() {
  return {
    status: SYNC_STATUS.IDLE,
    statusLabel: "Online",
    total: 0,
    synced: 0,
    remaining: 0,
    percent: 0,
    agencyTotal: 0,
    bankTotal: 0,
    agencySynced: 0,
    bankSynced: 0,
    lastSuccessAt: null,
    error: "",
    warning: "",
    initialComplete: false,
    ready: false,
    dbName: "",
  };
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      /* ignore */
    }
  });
}

function setSnapshot(partial) {
  snapshot = { ...snapshot, ...partial };
  emit();
}

export function getOfflineSyncSnapshot() {
  return snapshot;
}

export function subscribeOfflineSync(fn) {
  listeners.add(fn);
  fn(snapshot);
  return () => listeners.delete(fn);
}

export function stopOfflineSync() {
  runId += 1;
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}

function statusLabel(state) {
  if (state.status === SYNC_STATUS.SYNCING) return "Syncing";
  if (state.status === SYNC_STATUS.OFFLINE_READY) return "Offline Ready";
  if (state.status === SYNC_STATUS.COMPLETED) return "Sync Completed";
  if (state.status === SYNC_STATUS.FAILED) return "Sync Failed";
  return "Offline";
}

function computeProgress(state) {
  const total = Number(state.agencyTotal || 0) + Number(state.bankTotal || 0);
  const synced = Number(state.agencySynced || 0) + Number(state.bankSynced || 0);
  const remaining = Math.max(0, total - synced);
  const percent =
    total > 0 ? Math.min(99, Math.floor((synced / total) * 100)) : 0;
  return { total, synced, remaining, percent };
}

function toUiState(meta, extra = {}) {
  const progress = computeProgress(meta);
  const ready =
    meta.status === SYNC_STATUS.OFFLINE_READY && Boolean(meta.initialComplete);
  const percent = ready ? 100 : progress.percent;
  return {
    ...progress,
    percent,
    status: meta.status,
    statusLabel: statusLabel(meta),
    agencyTotal: meta.agencyTotal || 0,
    bankTotal: meta.bankTotal || 0,
    agencySynced: meta.agencySynced || 0,
    bankSynced: meta.bankSynced || 0,
    lastSuccessAt: meta.lastSuccessAt || null,
    error: meta.error || "",
    warning: meta.warning || "",
    initialComplete: Boolean(meta.initialComplete),
    ready,
    dbName: extra.dbName || snapshot.dbName,
  };
}

function defaultMeta(userId, companyId) {
  return {
    key: META_KEY,
    userId,
    companyId,
    status: SYNC_STATUS.IDLE,
    phase: "records-agency",
    agencyCursor: "",
    bankCursor: "",
    agencyIdCursor: "",
    bankIdCursor: "",
    agencySynced: 0,
    bankSynced: 0,
    agencyTotal: 0,
    bankTotal: 0,
    since: "",
    lastSuccessAt: null,
    lastStartedAt: null,
    initialComplete: false,
    error: "",
    warning: "",
    generation: 0,
    forceFullResync: false,
  };
}

async function saveMeta(db, meta) {
  await setMeta(db, meta);
  setSnapshot(toUiState(meta, { dbName: db.name }));
}

function assertRun(current) {
  if (current !== runId) {
    const err = new Error("stale-sync");
    err.code = "STALE_SYNC";
    throw err;
  }
}

function throwIfAborted(signal) {
  if (signal?.aborted) {
    const err = new Error("aborted");
    err.code = "ABORTED";
    throw err;
  }
}

async function fetchKeepIds(token, source, signal, current) {
  const keep = new Set();
  let cursor = "";
  let hasNext = true;
  while (hasNext) {
    assertRun(current);
    throwIfAborted(signal);
    const res = await offlineSyncService.getIds(
      token,
      { source, cursor, limit: 2000 },
      { signal }
    );
    (res?.ids || []).forEach((id) => keep.add(String(id)));
    hasNext = Boolean(res?.hasNext);
    cursor = res?.nextCursor || "";
    if (hasNext && !cursor) break;
  }
  return keep;
}

async function syncSourceRecords({
  db,
  token,
  source,
  meta,
  signal,
  current,
}) {
  const cursorKey = source === "bank" ? "bankCursor" : "agencyCursor";
  const syncedKey = source === "bank" ? "bankSynced" : "agencySynced";
  let cursor = meta[cursorKey] || "";
  let hasNext = true;
  const since = meta.initialComplete ? meta.since || "" : "";

  while (hasNext) {
    assertRun(current);
    throwIfAborted(signal);
    const res = await offlineSyncService.getRecords(
      token,
      { source, cursor, since, limit: RECORD_BATCH_LIMIT },
      { signal }
    );
    const items = Array.isArray(res?.items) ? res.items : [];
    const stored = [];
    const seen = new Set();
    for (const item of items) {
      const row = toStoredVehicle(item);
      if (!row.id || seen.has(row.id)) continue;
      seen.add(row.id);
      stored.push(row);
    }
    if (stored.length) {
      await putVehicleBatch(db, stored);
    }

    hasNext = Boolean(res?.hasNext);
    cursor = res?.nextCursor || "";
    meta[cursorKey] = cursor;
    meta[syncedKey] = await countVehiclesBySource(db, source);
    meta.error = "";
    await saveMeta(db, meta);
    if (hasNext && !cursor) break;
  }

  meta[syncedKey] = await countVehiclesBySource(db, source);
  meta[cursorKey] = "";
  await saveMeta(db, meta);
}

export async function hydrateOfflineSync(user) {
  const { userId, companyId } = resolveRepoIdentity(user);
  if (!userId || !companyId) {
    setSnapshot(emptySnapshot());
    return;
  }
  const dbName = offlineDbName(userId, companyId);
  try {
    const db = await openOfflineDb(dbName);
    const meta = (await getMeta(db, META_KEY)) || defaultMeta(userId, companyId);
    const localCount = await countVehicles(db);
    if (localCount && meta.agencySynced + meta.bankSynced === 0) {
      meta.agencySynced = localCount;
    }
    setSnapshot(toUiState(meta, { dbName }));
  } catch {
    setSnapshot(emptySnapshot());
  }
}

export async function startOfflineSync({ token, user }) {
  const { userId, companyId } = resolveRepoIdentity(user);
  if (!token || !userId || !companyId) {
    setSnapshot({
      ...emptySnapshot(),
      status: SYNC_STATUS.FAILED,
      statusLabel: "Sync Failed",
      error: "Missing user or company for offline sync.",
    });
    return;
  }

  stopOfflineSync();
  const current = runId;
  abortController = new AbortController();
  const { signal } = abortController;
  const dbName = offlineDbName(userId, companyId);

  let db;
  try {
    db = await openOfflineDb(dbName);
  } catch (error) {
    setSnapshot({
      ...emptySnapshot(),
      status: SYNC_STATUS.FAILED,
      statusLabel: "Sync Failed",
      error: isQuotaError(error)
        ? "This browser does not have enough storage for offline vehicle data."
        : "Could not open local vehicle database.",
    });
    return;
  }

  let meta = (await getMeta(db, META_KEY)) || defaultMeta(userId, companyId);
  if (meta.userId !== userId || meta.companyId !== companyId) {
    meta = defaultMeta(userId, companyId);
  }
  meta.generation = current;
  meta.status = SYNC_STATUS.SYNCING;
  meta.error = "";
  meta.warning = meta.initialComplete
    ? ""
    : "Complete dataset is not yet available offline.";
  meta.lastStartedAt = new Date().toISOString();
  if (meta.forceFullResync) {
    meta.since = "";
    meta.forceFullResync = false;
    meta.agencyCursor = "";
    meta.bankCursor = "";
  } else if (meta.initialComplete && meta.lastSuccessAt) {
    const sinceDate = new Date(meta.lastSuccessAt).getTime() - OVERLAP_MS;
    meta.since = new Date(Math.max(0, sinceDate)).toISOString();
  } else {
    meta.since = "";
  }
  await saveMeta(db, meta);

  try {
    try {
      const recipientsRes = await confirmationService.getWhatsAppRecipients(token);
      const recipients = Array.isArray(recipientsRes?.data?.adminRecipients)
        ? recipientsRes.data.adminRecipients
        : Array.isArray(recipientsRes?.adminRecipients)
          ? recipientsRes.adminRecipients
          : [];
      if (recipients.length) {
        saveWhatsAppRecipients({ userId, companyId, recipients });
      }
    } catch {
      /* keep previously cached admin / role numbers */
    }

    const summary = await offlineSyncService.getSummary(token, { signal });
    meta.agencyTotal = Number(summary?.agencyTotal) || 0;
    meta.bankTotal = Number(summary?.bankTotal) || 0;
    await saveMeta(db, meta);

    const phase = meta.phase || "records-agency";
    const order = [
      "records-agency",
      "records-bank",
      "prune-agency",
      "prune-bank",
      "verify",
    ];
    const startAt = order.includes(phase) ? order.indexOf(phase) : 0;

    for (let i = startAt; i < order.length; i += 1) {
      assertRun(current);
      meta.phase = order[i];
      await saveMeta(db, meta);

      if (order[i] === "records-agency") {
        await syncSourceRecords({
          db,
          token,
          source: "agency",
          meta,
          signal,
          current,
        });
      } else if (order[i] === "records-bank") {
        await syncSourceRecords({
          db,
          token,
          source: "bank",
          meta,
          signal,
          current,
        });
      } else if (order[i] === "prune-agency") {
        const keep = await fetchKeepIds(token, "agency", signal, current);
        await pruneVehiclesNotInSet(db, "agency", keep);
        meta.agencyIdCursor = "";
        meta.agencySynced = await countVehiclesBySource(db, "agency");
        await saveMeta(db, meta);
      } else if (order[i] === "prune-bank") {
        const keep = await fetchKeepIds(token, "bank", signal, current);
        await pruneVehiclesNotInSet(db, "bank", keep);
        meta.bankIdCursor = "";
        meta.bankSynced = await countVehiclesBySource(db, "bank");
        await saveMeta(db, meta);
      } else if (order[i] === "verify") {
        meta.agencySynced = await countVehiclesBySource(db, "agency");
        meta.bankSynced = await countVehiclesBySource(db, "bank");
        const localCount = meta.agencySynced + meta.bankSynced;
        const latest = await offlineSyncService.getSummary(token, { signal });
        meta.agencyTotal = Number(latest?.agencyTotal) || 0;
        meta.bankTotal = Number(latest?.bankTotal) || 0;
        const expected = meta.agencyTotal + meta.bankTotal;
        if (localCount !== expected) {
          meta.status = SYNC_STATUS.FAILED;
          meta.error = `Could not verify complete dataset (local ${localCount.toLocaleString("en-IN")}, server ${expected.toLocaleString("en-IN")}).`;
          meta.warning = "Complete dataset is not yet available offline.";
          meta.phase = "records-agency";
          meta.agencyCursor = "";
          meta.bankCursor = "";
          meta.forceFullResync = true;
          await saveMeta(db, meta);
          return;
        }
        meta.status = SYNC_STATUS.OFFLINE_READY;
        meta.initialComplete = true;
        meta.lastSuccessAt = new Date().toISOString();
        meta.warning = "";
        meta.error = "";
        meta.phase = "records-agency";
        await saveMeta(db, meta);
        setSnapshot({
          ...toUiState(meta, { dbName }),
          percent: 100,
          synced: localCount,
          remaining: 0,
          ready: true,
        });
      }
    }
  } catch (error) {
    if (error?.code === "STALE_SYNC" || error?.code === "ABORTED") return;
    const quota = isQuotaError(error);
    const expired = error?.response?.status === 401;
    meta.status = SYNC_STATUS.FAILED;
    meta.error = quota
      ? "This device does not have enough storage for the complete vehicle dataset."
      : expired
        ? "Session expired. Sign in again to finish offline sync."
        : error?.response?.data?.message ||
          error?.message ||
          "Sync failed. You can retry or resume.";
    meta.warning = meta.initialComplete
      ? ""
      : "Complete dataset is not yet available offline.";
    try {
      await saveMeta(db, meta);
    } catch {
      setSnapshot({
        ...toUiState(meta, { dbName }),
        status: SYNC_STATUS.FAILED,
        statusLabel: "Sync Failed",
        error: meta.error,
      });
    }
  }
}

export async function wipeOfflineCaches() {
  stopOfflineSync();
  clearWhatsAppRecipients();
  const known = readRememberedOfflineDbName();
  rememberOfflineDbName("");
  await deleteOfflineDatabase(known);
  setSnapshot(emptySnapshot());
}
